import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from '@lims/shared-validation';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import { currentLocale } from '../../i18n/index.js';
import { ISSUE_KEYS } from './useZodForm.js';
import { useToast } from './Toast.js';

/**
 * Saving a form: the ONE way every form in the product reports a save.
 *
 * WHY IT IS SHARED. Each form used to decide for itself which failures to
 * show. The project form knew three detail codes and rendered nothing for any
 * other, so a refused save (a 400 for an empty optional field) left the user
 * looking at a form that had silently not saved. The rules below hold for
 * every form that uses this module:
 *
 *  - a failure is ALWAYS shown, in the user's language, from the API's error
 *    CODE — never the server's English prose, never nothing;
 *  - every field the server named is marked, with a translated message;
 *  - the spinner stops when the request settles (the API client gives up after
 *    a bounded wait, see `REQUEST_TIMEOUT_MS`), and what was typed is kept;
 *  - success says so, and goes where the navigation rule says.
 */

/** A business rule the server reports against one field, beyond plain
 *  validation. Looked up under `fieldErrors.<code>` first. */
function fieldMessage(code: string | undefined, t: TFunction): string {
  const fallbackKey = (code && ISSUE_KEYS[code]) || 'validation.invalid';
  if (!code) return t(fallbackKey);
  return t(`fieldErrors.${code}`, { defaultValue: t(fallbackKey) });
}

/**
 * Codes that say "not in the right form". A page may give a field its own
 * sentence for these (`messages`) — "an IBAN's check digits do not match"
 * says more than "this value is not acceptable" — while a length or a
 * required value keeps the shared wording.
 */
const FORMAT_CODES = new Set(['custom', 'invalid_string', 'invalid_union']);

/**
 * A refusal that names the tests it is about (D325: `tests` on the detail, read
 * by the server from the catalogue): `fieldErrors.<code>One` or `<code>Many`
 * with the tests listed, each by its standard, else its code, else its name.
 * Null when the detail names none or no such sentence exists.
 */
function namedTestsMessage(detail: ApiError['details'][number], t: TFunction): string | null {
  const locale = currentLocale();
  const labels = (detail.tests ?? [])
    .map(
      (test) =>
        test.referenceStandard ??
        test.code ??
        (locale === 'ar' ? (test.nameAr ?? test.nameEn) : (test.nameEn ?? test.nameAr)),
    )
    .filter((label): label is string => Boolean(label));
  if (!detail.code || labels.length === 0) return null;
  // Each name isolated (FSI … PDI): a Latin standard inside Arabic text would
  // otherwise run into its neighbour and read out of order.
  const tests = new Intl.ListFormat(locale, { type: 'conjunction' }).format(
    labels.map((label) => `⁨${label}⁩`),
  );
  const key = `fieldErrors.${detail.code}${labels.length === 1 ? 'One' : 'Many'}`;
  return t(key, { tests, defaultValue: '' }) || null;
}

/** The message for one detail: the page's own for a format rule, else the shared one. */
function detailMessage(
  detail: ApiError['details'][number],
  t: TFunction,
  messages: Readonly<Record<string, string>>,
): string {
  const named = namedTestsMessage(detail, t);
  if (named) return named;
  const own =
    detail.field && detail.code && FORMAT_CODES.has(detail.code)
      ? messages[detail.field]
      : undefined;
  return own ?? fieldMessage(detail.code, t);
}

/** The translated headline for a failed save. */
export function saveErrorMessage(error: unknown, t: TFunction): string {
  const code = error instanceof ApiError ? error.code : 'unknown';
  return t(`errors.${code}`, { defaultValue: t('errors.unknown') });
}

/**
 * Field name to translated message, from the API's per-field details.
 *
 * A detail without a field (a whole-record rule) is not lost: it is returned
 * under the empty key, and the summary shows it.
 */
export function saveFieldErrors(
  error: unknown,
  t: TFunction,
  messages: Readonly<Record<string, string>> = {},
): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const output: Record<string, string> = {};
  for (const detail of error.details) {
    const field = detail.field ?? '';
    // The first problem per field is the one shown; a second one for the same
    // field would only repeat the instruction to look at it.
    if (output[field] === undefined) output[field] = detailMessage(detail, t, messages);
  }
  return output;
}

/**
 * Check a request body against the SHARED schema the API validates with, and
 * return field name to translated message. Empty when it passes.
 *
 * A courtesy that saves a round trip — the server checks again regardless.
 * An empty required value reads "required", not "too short".
 */
export function validationErrors(
  schema: z.ZodTypeAny,
  body: unknown,
  t: TFunction,
): Record<string, string> {
  const result = schema.safeParse(body);
  if (result.success) return {};
  const output: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const field = String(issue.path[0] ?? '');
    if (output[field] !== undefined) continue;
    const isEmpty =
      issue.code === 'too_small' && 'minimum' in issue && Number(issue.minimum) === 1;
    output[field] = t(
      isEmpty ? 'validation.required' : (ISSUE_KEYS[issue.code] ?? 'validation.invalid'),
    );
  }
  return output;
}

/** A value that settles `delay` ms after it stops changing. */
export function useDebouncedValue<Value>(value: Value, delay = 400): Value {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

/**
 * Every problem the save reported, as sentences for the summary.
 *
 * A problem on a field the page names reads "Label: message". One on a field
 * the page does not name (a line inside a list, a whole-record rule) is still
 * shown when its code has a message of its own — that is how a business rule
 * like "the quotation is not accepted" reaches the user. Codes without one
 * (a duplicate's confidence level, a raw path) are left to the headline and
 * to the part of the page that knows how to show them.
 */
export function saveProblems(
  error: unknown,
  t: TFunction,
  labels: Readonly<Record<string, string>>,
  messages: Readonly<Record<string, string>> = {},
): string[] {
  if (!(error instanceof ApiError)) return [];
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const detail of error.details) {
    // One problem per field, the same one the field itself shows.
    if (detail.field) {
      if (seen.has(detail.field)) continue;
      seen.add(detail.field);
    }
    const text = detailMessage(detail, t, messages);
    const label = detail.field ? labels[detail.field] : undefined;
    const specific =
      detail.code !== undefined &&
      (t(`fieldErrors.${detail.code}`, { defaultValue: '' }) !== '' ||
        ISSUE_KEYS[detail.code] !== undefined);
    const sentence = label ? `${label}: ${text}` : specific ? text : null;
    if (sentence && !problems.includes(sentence)) problems.push(sentence);
  }
  return problems;
}

/** True when the failed save carries this detail code (a business rule). */
export function hasSaveDetail(error: unknown, code: string): boolean {
  return (
    error instanceof ApiError &&
    error.details.some((detail) => detail.code === code)
  );
}

export interface UseSaveMutationOptions<Saved> {
  readonly mutationFn: () => Promise<Saved>;
  /** Translated. Shown as a success toast once the server has confirmed. */
  readonly successMessage: string;
  /**
   * Queries whose data this save changed. Active ones are refetched before
   * navigating; inactive ones are DROPPED, so the page the user lands on
   * loads the new value instead of flashing the old one from the cache.
   */
  readonly invalidate?: readonly QueryKey[];
  /**
   * Where to go once saved (the navigation rule):
   * create -> the new record's own page; edit -> back to its page, or to the
   * list when the record has no page of its own. `null` stays put.
   */
  readonly navigateTo?: (saved: Saved) => string | null;
  readonly onSaved?: (saved: Saved) => void;
}

export function useSaveMutation<Saved>({
  mutationFn,
  successMessage,
  invalidate = [],
  navigateTo,
  onSaved,
}: UseSaveMutationOptions<Saved>) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { show } = useToast();

  return useMutation({
    mutationFn,
    onSuccess: async (saved) => {
      for (const queryKey of invalidate) {
        queryClient.removeQueries({ queryKey, type: 'inactive' });
      }
      await Promise.all(
        invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      show({ tone: 'success', message: successMessage });
      onSaved?.(saved);
      const target = navigateTo?.(saved);
      if (target) navigate(target, { replace: true });
    },
  });
}

/**
 * The save's error as the form shows it: a headline and the marked fields.
 * `messages` gives a field its own sentence for a format rule (see
 * `FORMAT_CODES`).
 */
export function useSaveErrors(
  error: unknown,
  messages: Readonly<Record<string, string>> = {},
) {
  const { t } = useTranslation();
  return {
    message: error ? saveErrorMessage(error, t) : null,
    fields: error ? saveFieldErrors(error, t, messages) : {},
  };
}
